import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useT } from '../i18n';
import './DashboardBanner.css';

// ── THE DASHBOARD BANNER ─────────────────────────────────────────────────────
// A mid-height carousel across the top of the feed, above the recommended
// designers. Interior wallpapers, one at a time, advancing on their own.
//
// WHY THESE ARE LOCAL FILES. Uploaded as ~1.7MB PNGs (Images/wallpapper*.png in
// Storage) — five megabytes for a decorative strip on a page people open every
// day. Re-encoded to WebP they come to 296KB for all three with no visible
// loss, and served from the same origin as the page they skip a DNS lookup and
// a TLS handshake. Same trade as the landing hero: swapping a picture now means
// replacing the file and deploying, rather than re-uploading to Storage.
//
// EACH SLIDE NAMES A FEATURE. The pictures are of rooms, and a room on its own
// says "interiors" and nothing more — the caption is what makes the banner tell
// somebody what this app actually does. In order: AI staging of an uploaded
// photo, a photoreal render from a 3D room, the 2D floor plan, finding a
// designer, and the shops the furniture and materials come from.
//
// TRANSLATION KEYS, not literals: this strip is the first thing on the page a
// signed-in user opens every day, so it cannot be the one piece of English on
// an Armenian dashboard.
//
// ADDING ANOTHER: drop bannerN.webp beside the others and add a row here with
// its two keys. Everything below counts the array — the dots, the
// auto-advance and the arrows all size themselves from it.
const SLIDES = [
  { src: '/images/banner/banner1.webp', titleKey: 'dbn1_title', bodyKey: 'dbn1_body' },
  { src: '/images/banner/banner2.webp', titleKey: 'dbn2_title', bodyKey: 'dbn2_body' },
  { src: '/images/banner/banner3.webp', titleKey: 'dbn3_title', bodyKey: 'dbn3_body' },
  { src: '/images/banner/banner4.webp', titleKey: 'dbn4_title', bodyKey: 'dbn4_body' },
  { src: '/images/banner/banner5.webp', titleKey: 'dbn5_title', bodyKey: 'dbn5_body' },
];

// Slow enough to look at, quick enough that a visitor who stays a moment sees
// there is more than one.
const DWELL_MS = 5500;

export default function DashboardBanner() {
  const { t } = useT();
  const trackRef = useRef(null);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  // Set while THIS component is scrolling the track, so the scroll handler can
  // tell its own animation from a finger and not fight it.
  const selfScroll = useRef(false);

  const goTo = useCallback((i) => {
    const el = trackRef.current;
    if (!el) return;
    const next = ((i % SLIDES.length) + SLIDES.length) % SLIDES.length;
    selfScroll.current = true;
    el.scrollTo({ left: el.clientWidth * next, behavior: 'smooth' });
    setIndex(next);
    // Long enough for a smooth scroll to finish; the flag only suppresses the
    // index bookkeeping below, so an early release is harmless.
    setTimeout(() => { selfScroll.current = false; }, 600);
  }, []);

  // ── SWIPING PAST THE END WRAPS ─────────────────────────────────────────────
  // The arrows and the timer wrap for free: they go through `goTo`, which takes
  // the index modulo the slide count and scrolls wherever it likes. A FINGER
  // CANNOT. The track is a real scroll container, so at the last slide there is
  // simply nothing further to scroll to — the swipe does nothing and the
  // carousel feels stuck, even though the same action from the arrow works.
  //
  // There is no "tried to scroll past the end" event to listen for, so the
  // gesture is read directly: note at touchstart whether the track is already
  // parked at an edge, and on release, if it is STILL at that edge and the
  // finger travelled far enough in the direction that would have advanced,
  // treat it as the wrap it was meant to be.
  //
  // Both conditions matter. Without "was at the edge on touchstart" a fast
  // flick that lands on the last slide would wrap immediately past it; without
  // "still at the edge on release" an ordinary swipe from the middle would be
  // caught the moment it happened to end at one.
  const SWIPE_WRAP_PX = 45;
  const touch = useRef(null);

  const atEdges = () => {
    const el = trackRef.current;
    if (!el) return { start: false, end: false };
    // A couple of pixels of slack: scroll positions are fractional, and a
    // snapped slide rarely lands on an exact integer.
    return {
      start: el.scrollLeft <= 2,
      end: el.scrollLeft >= el.scrollWidth - el.clientWidth - 2,
    };
  };

  const onTouchStart = (e) => {
    if (e.touches.length !== 1) { touch.current = null; return; }
    const { clientX, clientY } = e.touches[0];
    touch.current = { x: clientX, y: clientY, ...atEdges() };
  };

  const onTouchEnd = (e) => {
    const from = touch.current;
    touch.current = null;
    if (!from) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - from.x;
    const dy = t.clientY - from.y;
    // Deliberate, and horizontal: a short drag is a tap that moved, and a
    // mostly-vertical one is the page being scrolled past the banner.
    if (Math.abs(dx) < SWIPE_WRAP_PX || Math.abs(dx) <= Math.abs(dy)) return;

    const now = atEdges();
    // Finger left = content advances. At the last slide that means "next",
    // which wraps to the first.
    if (from.end && now.end && dx < 0) goTo(0);
    else if (from.start && now.start && dx > 0) goTo(SLIDES.length - 1);
  };

  // ── THE INDEX FOLLOWS THE SCROLL ───────────────────────────────────────────
  // The track is a real scroller, so a swipe changes which slide is showing
  // without going through `goTo` at all. Reading the position back is what
  // keeps the dots honest about where a finger left it.
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return undefined;
    let frame = 0;
    const onScroll = () => {
      if (selfScroll.current) return;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const w = el.clientWidth || 1;
        setIndex(Math.round(el.scrollLeft / w));
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(frame);
    };
  }, []);

  // ── AUTO-ADVANCE ───────────────────────────────────────────────────────────
  // Stopped while the pointer is over it (someone looking at a picture should
  // not have it taken away), while the tab is hidden, and entirely under
  // reduced motion — a panel that slides on a timer is exactly what that
  // preference is about. The arrows and dots keep working either way.
  const reduced = useRef(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    reduced.current = mq.matches;
    const onChange = () => { reduced.current = mq.matches; };
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  useEffect(() => {
    if (paused || reduced.current || SLIDES.length < 2) return undefined;
    const id = setInterval(() => {
      if (!document.hidden) goTo(index + 1);
    }, DWELL_MS);
    return () => clearInterval(id);
  }, [paused, index, goTo]);

  return (
    <div
      className="dbn"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* A REAL SCROLLER WITH SNAP POINTS, not slides moved by transform.
          Swipe, momentum and keyboard scrolling all come from the browser, and
          there is no animation state that can be left half-finished. */}
      <div
        className="dbn-track"
        ref={trackRef}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        // A cancelled touch (a call arriving, the browser taking over the
        // gesture) must not leave a stale start point for the next one to
        // measure against.
        onTouchCancel={() => { touch.current = null; }}
      >
        {SLIDES.map((s, i) => (
          <div className="dbn-slide" key={s.src}>
            <img
              className="dbn-img"
              src={s.src}
              // The caption says what the picture shows, so the image itself
              // adds nothing a screen reader needs — decorative, and the words
              // below are real text rather than baked into the file.
              alt=""
              // The first is what the page paints with; the rest can wait until
              // they are scrolled toward.
              loading={i === 0 ? 'eager' : 'lazy'}
              fetchpriority={i === 0 ? 'high' : 'low'}
              decoding="async"
              draggable={false}
            />
            <div className="dbn-copy">
              <h3 className="dbn-title">{t(s.titleKey)}</h3>
              <p className="dbn-body">{t(s.bodyKey)}</p>
            </div>
          </div>
        ))}
      </div>

      {SLIDES.length > 1 && (
        <>
          {/* Pointer devices only — a phone swipes, and two buttons parked over
              the picture would cover a third of it (see the CSS).

              SVG CHEVRONS, NOT ‹ AND ›. Those are text, and a glyph's ink is
              not centred in its em box — the character sits on the baseline
              with its own side bearings, so `place-items: center` centred the
              LINE BOX and left the visible mark high and off to one side. The
              button itself was always exactly centred; the arrow inside it was
              not. A path is geometry: it is centred because its coordinates
              say so, in any font, at any size. */}
          <button
            type="button"
            className="dbn-nav dbn-nav--prev"
            onClick={() => goTo(index - 1)}
            aria-label="Previous"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
              strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M15 5 8 12l7 7" />
            </svg>
          </button>
          <button
            type="button"
            className="dbn-nav dbn-nav--next"
            onClick={() => goTo(index + 1)}
            aria-label="Next"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
              strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M9 5l7 7-7 7" />
            </svg>
          </button>

          <div className="dbn-dots">
            {SLIDES.map((s, i) => (
              <button
                key={s.src}
                type="button"
                className={`dbn-dot${i === index ? ' is-on' : ''}`}
                onClick={() => goTo(i)}
                aria-label={`Slide ${i + 1}`}
                aria-current={i === index}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
